import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box, Fade, Tooltip } from "@mui/material";
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Close as CloseIcon,
  Visibility as InspectIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  SmartToy as RobotIcon,
  Chat as ChatIcon,
  DirectionsCar as ParkIcon,
} from "@mui/icons-material";
import useChatSignalR from "./hooks/useChatSignalR";
import useAgentChat from "./hooks/useAgentChat";
import useExplainMode from "./hooks/useExplainMode";
import { ModeToggle } from "./components/ModeToggle";
import { ChatMessageItem } from "./components/ChatMessageItem";
import { ChatComposer } from "./components/ChatComposer";
import { ParkedChatsPanel } from "./components/ParkedChatsPanel";
import { ChatMessage, ChatMode } from "./types";
import { ElementExplainContext } from "./explainUtils";
import {
  chatbotEnabled,
  chatbotEnableVoiceDocs,
  chatbotEnableVoiceAgent,
  chatbotTranscribeUrlDocs,
  chatbotTranscribeUrlAgent,
  chatbotConfig,
  chatbotExplainShortcutKey,
} from "./config";
import {
  type AttachedImage,
  collectImageDataUrls,
} from "./utils/imageAttachment";
import {
  loadParkedChats,
  parkChat,
  removeParkedChat,
  getParkedChatByThreadId,
  type ParkedThread,
} from "./utils/parkedStorage";
import {
  ChatToggleButton,
  ChatPanel,
  ChatHeader,
  MinimizedBar,
  HeaderButton,
  MessagesContainer,
  MessageWrapper,
  MessageBubble,
  MessageTimestamp,
  Toolbar,
  ToolbarButton,
  StatusDot,
  ExplainToggleFAB,
} from "./styles";

interface ChatBotProps {
  serverUrl?: string;
  tenantId?: string;
  assistantId?: string;
  agentName?: string;
  /** Enable voice input in Docs mode (transcript sent as text to SignalR hub). */
  enableVoiceDocs?: boolean;
  /** Enable voice input in Agent mode (transcript sent with agent execute). */
  enableVoiceAgent?: boolean;
  /** POST endpoint for speech-to-text in Docs mode. */
  transcribeUrlDocs?: string;
  /** POST endpoint for speech-to-text in Agent mode. */
  transcribeUrlAgent?: string;
  enabled?: boolean;
  /** Locale for chat (e.g. from app's useCurrentPerson). Default 'en'. */
  locale?: string;
  /** When true, the ChatBot is controlled externally (no floating button) */
  open?: boolean;
  /** Callback when user closes the chat panel */
  onClose?: () => void;
  /** Called when user triggers AI-Explain from a data-ai-explain element (e.g. to open chat) */
  onExplainRequest?: () => void;
    /** Which side of the screen to anchor the chat panel. Default 'left'. */
  align?: 'left' | 'right';
  /**
   * When true (default), the current page context is prepended to docs chat messages so
   * the AI can answer page-specific questions without the user enabling explain mode.
   * Pass pageContextEnabled={false} to disable. Falls back to window.location.href
   * when no currentPageContext is provided.
   */
  pageContextEnabled?: boolean;
  /**
   * Human-readable description of the current page (e.g. "Resource Admin - Applications page").
   * When omitted, window.location.href is used as the fallback context.
   */
  currentPageContext?: string;
}

const ChatBot: React.FC<ChatBotProps> = ({
  serverUrl = chatbotConfig.serverUrl,
  tenantId = chatbotConfig.tenantId,
  assistantId = chatbotConfig.assistantId as string | undefined,
  agentName = chatbotConfig.agentName,
  enableVoiceDocs = chatbotEnableVoiceDocs,
  enableVoiceAgent = chatbotEnableVoiceAgent,
  transcribeUrlDocs = chatbotTranscribeUrlDocs,
  transcribeUrlAgent = chatbotTranscribeUrlAgent,
  enabled = chatbotEnabled,
  locale = "en",
  open,
  onClose,
  onExplainRequest,
  align = 'left',
  pageContextEnabled = true,
  currentPageContext,
}) => {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open : internalOpen;

  const showAgentMode = !!agentName;
  const [mode, setMode] = useState<ChatMode>("docs");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [parkedChats, setParkedChats] = useState<ParkedThread[]>(() => loadParkedChats());
  const [showParkedPanel, setShowParkedPanel] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const threadIdRef = useRef(`thread-${Date.now()}`);

  const handleOpen = () => {
    if (!isControlled) setInternalOpen(true);
  };

  const handleClose = () => {
    setIsMinimized(false);
    if (isControlled && onClose) {
      onClose();
    } else {
      setInternalOpen(false);
    }
  };

  const [explainModeActive, setExplainModeActive] = useState(false);
  const explainShortcutKey = chatbotExplainShortcutKey as string;
  const shortcutLabel =
    explainShortcutKey === "ctrlOrMeta"
      ? "Ctrl / ⌘ Cmd"
      : explainShortcutKey.length === 1
        ? explainShortcutKey.toUpperCase()
        : explainShortcutKey;

  // Both hooks are always called (React rules of hooks).
  // useChatSignalR only connects when isOpen AND mode === 'docs'.
  // Connect as soon as explain mode is active so the connection is ready
  // before the user clicks an element — even if the chat panel is closed.
  const docsChat = useChatSignalR({
    serverUrl,
    locale,
    tenantId,
    shouldConnect: (!!isOpen && mode === "docs") || explainModeActive,
  });

  const handleExplainElement = useCallback(
    (_context: ElementExplainContext, prompt: string) => {
      // Auto-open the chat panel if it isn't already open
      if (!isControlled) setInternalOpen(true);
      setIsMinimized(false);
      docsChat.sendMessage(
        prompt,
        assistantId || docsChat.selectedAssistant?.id,
        {
          silent: true,
        },
      );
      onExplainRequest?.();
    },
    [docsChat, assistantId, onExplainRequest, isControlled],
  );
  useExplainMode({
    active: explainModeActive,
    onExplainElement: handleExplainElement,
  });

  // Hold the configured shortcut key to activate explain mode; release to deactivate.
  // 'ctrlOrMeta' matches Ctrl on Windows/Linux and ⌘ Cmd on Mac.
  // Printable keys (single characters) are ignored when an editable element is focused
  // so typing normally never accidentally triggers explain mode.
  useEffect(() => {
    if (!enabled) return;
    const isEditableFocused = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName.toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    };
    const isModifierKey = explainShortcutKey === "ctrlOrMeta" || explainShortcutKey.length > 1;
    const matches = (e: KeyboardEvent) =>
      explainShortcutKey === "ctrlOrMeta"
        ? e.key === "Control" || e.key === "Meta"
        : e.key === explainShortcutKey;
    const onKeyDown = (e: KeyboardEvent) => {
      if (matches(e) && !e.repeat && (isModifierKey || !isEditableFocused()))
        setExplainModeActive(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (matches(e)) setExplainModeActive(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [enabled, explainShortcutKey]);

  const agentChat = useAgentChat({ agentName });

  const activeChat = mode === "docs" ? docsChat : agentChat;

  const { messages, connectionStatus, isLoading, configError } = activeChat;

  const isCurrentThreadParked =
    mode === "agent" && getParkedChatByThreadId(threadIdRef.current) !== null;

  const handleParkChat = useCallback(() => {
    if (mode !== "agent" || !agentName) return;
    const progress =
      agentChat.currentProgress ||
      agentChat.messages.find(m => m.isStreaming)?.streamingProgress || {
        status: "thinking",
        elapsed_ms: 30000,
        iteration: 1,
        max_iterations: 15,
        tools_completed: [],
        tools_pending: [],
      };
    const lastUserMessage =
      [...agentChat.messages]
        .reverse()
        .find(m => m.role === "user")
        ?.content || "Chat in progress…";

    parkChat({
      threadId: threadIdRef.current,
      agentName,
      displayName: agentName,
      progress,
      conversationId: agentChat.conversationId,
      runId: agentChat.currentRunId || undefined,
      lastUserMessage,
      messages: agentChat.messages,
    });

    setParkedChats(loadParkedChats());
    agentChat.cancelRequest();
    setShowParkedPanel(true);
  }, [mode, agentName, agentChat]);

  const handleResumeParkedChat = useCallback(
    (chat: ParkedThread) => {
      if (chat.thread_id !== threadIdRef.current) {
        threadIdRef.current = chat.thread_id;
      }
      agentChat.restoreConversation(chat.conversation_id, chat.messages_snapshot);
      removeParkedChat(chat.id);
      setParkedChats(loadParkedChats());
      setShowParkedPanel(false);
    },
    [agentChat],
  );

  const handleDismissParkedChat = useCallback((chatId: string) => {
    removeParkedChat(chatId);
    setParkedChats(loadParkedChats());
  }, []);

  const handleModeChange = useCallback(
    (nextMode: ChatMode) => {
      if (nextMode === mode) return;
      // Clear both conversations when switching so they don't bleed into each other
      docsChat.newChat();
      agentChat.newChat();
      threadIdRef.current = `thread-${Date.now()}`;
      setMode(nextMode);
      setInputValue("");
      setAttachedImages([]);
    },
    [mode, docsChat, agentChat],
  );

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    const hasValidImages = attachedImages.some(
      (img) => !img.error && img.preview,
    );
    if ((!trimmed && !hasValidImages) || isLoading) return;

    if (mode === "docs") {
      let messageToSend = trimmed;
      if (pageContextEnabled) {
        const pageContext = currentPageContext ?? window.location.href;
        const contextPrefix = `[Page context — use only if relevant to the question below]\nCurrent page: ${pageContext}\n\n`;
        messageToSend = contextPrefix + trimmed;
      }
      docsChat.sendMessage(
        messageToSend,
        assistantId || docsChat.selectedAssistant?.id,
        pageContextEnabled ? { displayMessage: trimmed } : undefined,
      );
    } else {
      const { dataUrls, error } = await collectImageDataUrls(attachedImages);
      if (error) {
        console.warn("[ChatBot]", error);
        return;
      }
      const previews = attachedImages
        .filter((img) => !img.error && img.preview)
        .map((img) => img.preview);
      agentChat.sendMessage(trimmed, {
        displayMessage: trimmed,
        images: dataUrls.length > 0 ? dataUrls : undefined,
        imagePreviews: previews.length > 0 ? previews : undefined,
      });
    }
    setInputValue("");
    setAttachedImages([]);
  }, [
    inputValue,
    attachedImages,
    isLoading,
    mode,
    docsChat,
    agentChat,
    assistantId,
    pageContextEnabled,
    currentPageContext,
  ]);

  const voiceEnabled =
    mode === "docs" ? enableVoiceDocs : enableVoiceAgent;
  const transcribeUrl =
    mode === "docs" ? transcribeUrlDocs : transcribeUrlAgent;
  const allowImages = mode === "agent";
  const inputDisabled =
    (mode === "docs" && connectionStatus !== "connected") || !!configError;
  const canSend =
    (!!inputValue.trim() ||
      attachedImages.some((img) => !img.error && img.preview)) &&
    !inputDisabled;

  const assistantLabel =
    mode === "docs"
      ? docsChat.selectedAssistant?.name || "EmpowerIQ Docs"
      : agentName;

  const welcomeText =
    mode === "docs"
      ? "Hello! I'm EmpowerIQ Docs. Ask me anything about the documentation..."
      : `Hello! I'm ${agentName}. I can run MyID actions on your behalf — ask me anything!`;

  if (!enabled) return null;

  return createPortal(
    <>
      {/* Always-visible explain toggle — accessible even when chat is minimized or closed */}
      <Tooltip
        title={
          explainModeActive
            ? `Explain ON — click any highlighted element or release ${shortcutLabel}`
            : `Explain mode: click to toggle, or hold ${shortcutLabel}`
        }
        placement="right"
        slotProps={{ popper: { style: { zIndex: 100000 } } }}
      >
        <ExplainToggleFAB
          isActive={explainModeActive}
          align={align}
          aria-pressed={explainModeActive}
          aria-label={explainModeActive ? "Explain mode on" : "Explain mode off"}
          onClick={() => setExplainModeActive((prev) => !prev)}
        >
          <InspectIcon />
        </ExplainToggleFAB>
      </Tooltip>

      {!isControlled && (
        <Fade in={!isOpen}>
          <ChatToggleButton onClick={handleOpen} aria-label="Open chat" align={align}>
            <ChatIcon
              sx={{ fontSize: 28, color: "var(--main-accent, #3b82f6)" }}
            />
          </ChatToggleButton>
        </Fade>
      )}

      <Fade in={isOpen && isMinimized}>
        <MinimizedBar
          onClick={() => setIsMinimized(false)}
          role="button"
          aria-label="Expand chat"
          align={align}
        >
          <RobotIcon
            sx={{ fontSize: 20, color: "var(--main-accent, #3b82f6)" }}
          />
          <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
            Chat
          </Box>
          <ExpandLessIcon
            sx={{ fontSize: 24, color: "var(--main-text-secondary, #64748b)" }}
          />
        </MinimizedBar>
      </Fade>

      <Fade in={isOpen && !isMinimized}>
        <ChatPanel isFullscreen={isFullscreen} elevation={8} align={align}>
          {/* Header */}
          <ChatHeader>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HeaderButton
                onClick={() => setIsFullscreen((f) => !f)}
                aria-label={
                  isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
                }
              >
                {isFullscreen ? (
                  <FullscreenExitIcon sx={{ fontSize: 16 }} />
                ) : (
                  <FullscreenIcon sx={{ fontSize: 16 }} />
                )}
                {isFullscreen ? "EXIT FULL SCREEN" : "FULL SCREEN"}
              </HeaderButton>
              <HeaderButton onClick={() => setIsMinimized(true)} aria-label="Minimize chat">
                <ExpandLessIcon sx={{ fontSize: 16 }} />
                MINIMIZE
              </HeaderButton>
              <HeaderButton onClick={handleClose} aria-label="Close chat">
                <CloseIcon sx={{ fontSize: 16 }} />
                CLOSE
              </HeaderButton>
            </Box>
          </ChatHeader>

          {/* Messages */}
          <MessagesContainer
            role="log"
            aria-live="polite"
            aria-label="Chat messages"
          >
            {configError && (
              <MessageWrapper isUser={false}>
                <MessageBubble isUser={false}>
                  <p style={{ color: "var(--error-color, #ef4444)" }}>
                    <strong>Configuration Error</strong>
                    <br />
                    {configError}
                  </p>
                </MessageBubble>
                <MessageTimestamp>
                  <StatusDot status="error" />
                  Not Available
                </MessageTimestamp>
              </MessageWrapper>
            )}

            {messages.length === 0 && !configError && (
              <MessageWrapper isUser={false}>
                <MessageBubble isUser={false}>
                  <p>{welcomeText}</p>
                </MessageBubble>
                <MessageTimestamp>
                  <StatusDot status={connectionStatus} />
                  {connectionStatus === "connected"
                    ? "Online"
                    : connectionStatus === "connecting"
                      ? "Connecting..."
                      : connectionStatus === "error"
                        ? "Connection Failed"
                        : "Offline"}
                </MessageTimestamp>
              </MessageWrapper>
            )}

            {messages.map((message: ChatMessage) => (
              <ChatMessageItem
                key={message.id}
                message={message}
                assistantLabel={assistantLabel}
                mode={mode}
                agentName={agentName}
                conversationId={agentChat.conversationId}
                onCancel={activeChat.cancelRequest}
                onPark={mode === "agent" ? handleParkChat : undefined}
                isParked={isCurrentThreadParked}
                onWaitingFormSubmit={(id, values) =>
                  agentChat.resumeWaitingForm(id, values)
                }
                onWaitingFormCancel={id => agentChat.cancelWaitingForm(id)}
                onElicitationComplete={id => agentChat.resumeAfterElicitation(id)}
                onRetryTool={(id, tool) => agentChat.retryTool(id, tool)}
                onTimeoutRetry={suggestion => agentChat.sendMessage(suggestion)}
              />
            ))}

            <div ref={messagesEndRef} />
          </MessagesContainer>

          {/* Input */}
          <ChatComposer
            value={inputValue}
            onChange={setInputValue}
            onSend={() => {
              void handleSend();
            }}
            onStop={() => activeChat.cancelRequest()}
            isLoading={isLoading}
            disabled={inputDisabled}
            canSend={canSend}
            placeholder={
              configError
                ? "Chat not available"
                : mode === "docs"
                  ? "Ask about the docs..."
                  : "Ask the AI agent..."
            }
            enableVoice={voiceEnabled}
            transcribeUrl={transcribeUrl}
            allowImages={allowImages}
            attachedImages={attachedImages}
            onAttachedImagesChange={setAttachedImages}
          />

          {/* Toolbar */}
          <Toolbar>
            <ModeToggle mode={mode} onChange={handleModeChange} showAgent={showAgentMode} />
            <Box sx={{ flex: 1 }} />
            {mode === "agent" && parkedChats.length > 0 && (
              <Box sx={{ position: "relative" }}>
                {showParkedPanel && (
                  <ParkedChatsPanel
                    chats={parkedChats}
                    onResume={handleResumeParkedChat}
                    onDismiss={handleDismissParkedChat}
                  />
                )}
                <ToolbarButton
                  onClick={() => setShowParkedPanel(v => !v)}
                  aria-label="Show parked chats"
                >
                  <ParkIcon />
                  PARKED ({parkedChats.length})
                </ToolbarButton>
              </Box>
            )}
            <ToolbarButton
              onClick={() => {
                threadIdRef.current = `thread-${Date.now()}`;
                activeChat.newChat();
              }}
              aria-label="Start new chat"
            >
              <AddIcon />
              NEW CHAT
            </ToolbarButton>
          </Toolbar>
        </ChatPanel>
      </Fade>
    </>,
    document.body,
  );
};

export default ChatBot;
